// store/currencySlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CurrencyCode } from '../../schemes/items';
import { baseUrl } from '../../api';

interface Currency {
    id: number;
    name_en: string;
    name_uz: string;
    name_ru: string;
    code: CurrencyCode;
}

interface CurrencyState {
    selected: CurrencyCode;
    available: Currency[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

// thunk: fetch available currencies from api
export const fetchCurrencies = createAsyncThunk<Currency[]>('currency/fetchCurrencies', async (_, { rejectWithValue }) => {
    try {
        const res = await fetch(`${baseUrl}/Items/GetCurrencies`);
        if (!res.ok) throw new Error(`http error ${res.status}`);
        return (await res.json()) as Currency[];
    } catch (err: any) {
        return rejectWithValue(err.message ?? 'failed to fetch currencies');
    }
});

const initialState: CurrencyState = {
    selected: 'UZS', // default fallback
    available: [],
    status: 'idle'
};

export const currencySlice = createSlice({
    name: 'currency',
    initialState,
    reducers: {
        setCurrency: (state, action: PayloadAction<CurrencyCode>) => {
            const exists = state.available.some((c) => c.code === action.payload);
            if (exists) state.selected = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrencies.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchCurrencies.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.available = action.payload;
                // if your default selected doesn’t exist in the new list, pick first
                if (!state.available.some((c) => c.code === state.selected)) {
                    state.selected = state.available[0]?.code ?? 'USD';
                }
            })
            .addCase(fetchCurrencies.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    }
});

export const { setCurrency } = currencySlice.actions;
export { selectSelectedCurrency, selectCurrencyAvailable, selectCurrencyStatus } from '../selectors';
export default currencySlice.reducer;
